import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiUrl, BaseUrls } from '@app/app.constants';
import { AgentState } from '@app/core/interfaces/c5/acd/agent-state';
import { EndUser, EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { EndUserData } from '@app/core/interfaces/end-user-data';
import { AddEndUser } from '@app/core/interfaces/portal/add-end-user';
import { UpdateUserData } from '@app/core/interfaces/portal/update-user-data';
import { UserListingData } from '@app/core/interfaces/portal/user-listing-data';
import { UserLevel } from '@app/core/models/user-level';
import { EnhancedEndUserLite } from '@app/shared/models/enhanced-end-user-lite';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { Device, DeviceKind } from '../interfaces/c5/device';

@Injectable({
  providedIn: 'root',
})
export class EndUserRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  create(userDefinition: AddEndUser): Observable<EndUser> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `${BaseUrls.ServiceProvider.Enterprise}/users`,
      [UserLevel.EnterpriseAdmin.toString()]: `${BaseUrls.Enterprise.Enterprise}/users`,
    };

    return this.utils.apiUrl(url).pipe(
      mergeMap((url) => this.http.post<EndUser>(url, userDefinition)),
      wrapError('end-user-repository:create-end-user')
    );
  }

  delete(id: number | string): Observable<void> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/users/:userId`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.EndUser,
    };

    return this.utils.apiUrl(url, { userId: id }).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('end-user-repository:delete-end-user')
    );
  }

  get(enterpriseId?: string | number, userId?: string | number): Observable<EndUserData> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/users/:userId?id=:userId`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.EndUser + `?id=:userId`,
      [UserLevel.EndUser.toString()]: BaseUrls.EndUser + '/ownData',
    };

    const params = {
      enterpriseId,
      userId,
    };

    return this.utils.apiUrl(url, params).pipe(
      mergeMap((url) => this.http.get<EndUserData>(url)),
      wrapError('end-user-repository:get')
    );
  }

  getC5User(): Observable<[EndUser, Device[]]> {
    return this.utils.apiUrl('/c5').pipe(
      mergeMap((url) => this.http.get<[EndUser, Device[]]>(url)),
      wrapError('end-user-repository:get-c5-user')
    );
  }

  list(enterpriseName?: string): Observable<EnhancedEndUserLite[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/endusers`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + `/endusers`,
    };

    return this.utils
      .apiUrl(urls, {
        enterpriseId: enterpriseName,
      })
      .pipe(
        mergeMap((url) => this.http.get<EndUserLite[]>(url)),
        map((users) =>
          users.map((user) => ({
            ...user,
            extension: user.loginName.split('@')[0],
          }))
        ),
        wrapError('end-user-repository:list')
      );
  }

  listUsers(enterpriseName?: string): Observable<UserListingData[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/users`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.Enterprise + `/users`,
    };

    return this.utils
      .apiUrl(urls, {
        enterpriseId: enterpriseName,
      })
      .pipe(
        mergeMap((url) => this.http.get<EndUserLite[]>(url)),
        map((users) =>
          users.map((user) => ({
            ...user,
            extension: user.loginName.split('@')[0],
          }))
        ),
        wrapError('end-user-repository:list')
      );
  }

  listWithDeviceName(deviceName: string): Observable<EndUserWithId[]> {
    const urls = {
      [UserLevel.ServiceProviderAdmin.toString()]:
        BaseUrls.ServiceProvider.Enterprise + `/endUsersWithDeviceName?deviceName=${deviceName}`,
      [UserLevel.EnterpriseAdmin.toString()]:
        BaseUrls.Enterprise.Enterprise + `/endUsersWithDeviceName?deviceName=${deviceName}`,
    };

    return this.utils.apiUrl(urls).pipe(mergeMap((url) => this.http.get<EndUserWithId[]>(url)));
  }

  update(payload: UpdateUserData): Observable<EndUser> {
    const url = {
      [UserLevel.ServiceProviderAdmin.toString()]: `/enterprise/:enterpriseId/users/:userId`,
      [UserLevel.EnterpriseAdmin.toString()]: BaseUrls.Enterprise.EndUser,
    };

    return this.utils.apiUrl(url, { userId: payload.user.loginName }).pipe(
      mergeMap((url) => this.http.put<EndUser>(url, payload)),
      wrapError('end-user-repository:update-end-user')
    );
  }

  updateAgentState(agentState: AgentState): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .put<void>(apiUrl('/user/acd/agent/state/all'), JSON.stringify(agentState), {
        headers,
      })
      .pipe(wrapError('end-user-repository:update-agent-state'));
  }

  updateDevicesIpFilterExclusion(loginName: string, deviceKinds: DeviceKind[], excludeIpFilter: boolean) {
    const params = {
      deviceKind: deviceKinds.join(','),
      exclude: excludeIpFilter,
    };
    return this.utils.apiUrl('/users/:userId/ipFilter/devices/exclusion', { userId: loginName }).pipe(
      mergeMap((url) =>
        this.http.put<void>(url, null, {
          params,
        })
      ),
      wrapError('end-user-repository:update-devices-ipfilter-exclusion')
    );
  }

  updatePassword(password: string): Observable<void> {
    return this.utils.apiUrl('/password').pipe(
      mergeMap((url) => this.http.put<void>(url, { loginPassword: password })),
      wrapError('end-user-repository:update-password')
    );
  }

  updatePin(pin: string, pinRetype: string): Observable<void> {
    return this.utils.apiUrl('/pin').pipe(
      mergeMap((url) =>
        this.http.put<void>(url, {
          pin,
          pinRetype,
        })
      ),
      wrapError('end-user-repository:update-pin')
    );
  }
}
